import type { CorePlugin } from './definitions';
import { CoreHuawei } from './huawei';
import type { GeolocationPlugin } from './definitions';
import { GeolocationHuawei } from './geolocation';
import type { DevicePlugin } from './definitions';
import { DeviceHuawei } from './device';
import type { PreferencesPlugin } from './definitions';
import { PreferencesHuawei } from './preferences';
import type { SharePlugin } from './definitions';
import { ShareHuawei } from './share';
import type { CameraPlugin } from './definitions';
import { CameraHuawei } from './camera';

export * from './definitions';
export { HuaweiBridgeClient } from './huawei-bridge-client';
export { CoreHuawei } from './huawei';
export { GeolocationHuawei } from './geolocation';
export { DeviceHuawei } from './device';
export { PreferencesHuawei } from './preferences';
export { ShareHuawei } from './share';
export { CameraHuawei } from './camera';

/**
 * Detect whether the current environment is a Huawei HarmonyOS WebView.
 * Cached after first detection to avoid repeated UA string scanning.
 *
 * Detection relies on User-Agent containing 'huawei' (case-insensitive).
 * The HarmonyOS native plugin appends "AigensSDK HuaWei" to the UA.
 */
let _isHuaWei: boolean | null = null;

export function isHuaWei(): boolean {
    if (_isHuaWei === null) {
        if (typeof window !== 'undefined' && window.isHuaWei === true) {
            _isHuaWei = true;
        } else if (typeof navigator !== 'undefined') {
            _isHuaWei = navigator.userAgent.toLowerCase().includes('huawei-aigens');
            // Cache on window for consistency with index.html detection
            if (typeof window !== 'undefined') {
                window.isHuaWei = _isHuaWei;
            }
        } else {
            _isHuaWei = false;
        }
    }
    return _isHuaWei;
}

/**
 * Get the Huawei Core plugin instance (singleton).
 * Use this in place of Capacitor's `Core` when `isHuaWei()` is true.
 */
let _coreHuawei: CoreHuawei | null = null;

export function getHuaweiCore(): CorePlugin {
    if (!_coreHuawei) {
        _coreHuawei = new CoreHuawei();
    }
    return _coreHuawei;
}

/**
 * Get the Huawei Geolocation plugin instance (singleton).
 * Use this in place of Capacitor's `Geolocation` when `isHuaWei()` is true.
 */
let _geoHuawei: GeolocationHuawei | null = null;

export function getHuaweiGeolocation(): GeolocationPlugin {
    if (!_geoHuawei) {
        _geoHuawei = new GeolocationHuawei();
    }
    return _geoHuawei;
}

/**
 * Get the Huawei Device plugin instance (singleton).
 * Use this in place of Capacitor's `Device` when `isHuaWei()` is true.
 */
let _deviceHuawei: DeviceHuawei | null = null;

export function getHuaweiDevice(): DevicePlugin {
    if (!_deviceHuawei) {
        _deviceHuawei = new DeviceHuawei();
    }
    return _deviceHuawei;
}

/**
 * Get the Huawei Preferences plugin instance (singleton).
 * Use this in place of Capacitor's `Preferences` when `isHuaWei()` is true.
 */
let _preferencesHuawei: PreferencesHuawei | null = null;

export function getHuaweiPreferences(): PreferencesPlugin {
    if (!_preferencesHuawei) {
        _preferencesHuawei = new PreferencesHuawei();
    }
    return _preferencesHuawei;
}

/**
 * Get the Huawei Share plugin instance (singleton).
 * Use this in place of Capacitor's `Share` when `isHuaWei()` is true.
 */
let _shareHuawei: ShareHuawei | null = null;

export function getHuaweiShare(): SharePlugin {
    if (!_shareHuawei) {
        _shareHuawei = new ShareHuawei();
    }
    return _shareHuawei;
}

/**
 * Get the Huawei Camera plugin instance (singleton).
 * Use this in place of Capacitor's `Camera` when `isHuaWei()` is true.
 */
let _cameraHuawei: CameraHuawei | null = null;

export function getHuaweiCamera(): CameraPlugin {
    if (!_cameraHuawei) {
        _cameraHuawei = new CameraHuawei();
    }
    return _cameraHuawei;
}
